// Single comment display component

import { useState } from 'react'
import { Link } from 'react-router'
import { Comment } from '@/types/comment'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { formatRelativeTime, formatReputation } from '@/utils/formatters'
import { ArrowUp, MessageCircle, DollarSign, Reply, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { ReplyForm } from './CommentForm'
import { EditCommentForm } from './EditCommentForm'
import { DeleteCommentDialog } from './DeleteCommentDialog'
import { useAuthStore } from '@/stores/authStore'

interface CommentItemProps {
  comment: Comment
  showReplies?: boolean
  rootAuthor?: string
  rootPermlink?: string
}

export function CommentItem({
  comment,
  showReplies = true,
  rootAuthor,
  rootPermlink,
}: CommentItemProps) {
  const [showReplyForm, setShowReplyForm] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const { isAuthenticated, user } = useAuthStore()

  // Check if current user is the comment author
  const isOwner = isAuthenticated && user?.username === comment.author

  // Check if comment can be modified (within 7 days)
  const canModify = () => {
    const created = new Date(comment.created)
    const now = new Date()
    const daysSinceCreation = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)
    return daysSinceCreation <= 7
  }

  // Check if comment can be deleted (no replies and within 7 days)
  const canDelete = () => {
    return canModify() && comment.childCount === 0
  }

  // Determine root author/permlink for nested replies
  const effectiveRootAuthor = rootAuthor || comment.rootAuthor
  const effectiveRootPermlink = rootPermlink || comment.rootPermlink

  const handleReplySuccess = () => {
    setShowReplyForm(false)
  }

  const handleEditSuccess = () => {
    setIsEditing(false)
  }

  const handleDeleteSuccess = () => {
    setShowDeleteDialog(false)
  }

  return (
    <div className="py-4">
      {/* Comment Header */}
      <div className="flex items-start gap-3">
        <Link to={`/@${comment.author}`}>
          <Avatar className="h-8 w-8">
            <AvatarImage
              src={`https://steemitimages.com/u/${comment.author}/avatar`}
              alt={comment.author}
            />
            <AvatarFallback className="text-xs">
              {comment.author[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </Link>

        <div className="flex-1 min-w-0">
          {/* Author and Metadata */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to={`/@${comment.author}`}
                className="font-semibold text-sm hover:underline"
              >
                {comment.author}
              </Link>
              <span className="text-xs text-muted-foreground">
                ({formatReputation(comment.authorReputation)})
              </span>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground">
                {formatRelativeTime(comment.created)}
              </span>
            </div>

            {/* Edit/Delete Menu for comment owner */}
            {isOwner && canModify() && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    aria-label="댓글 메뉴"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => setIsEditing(true)}
                    disabled={isEditing}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    수정
                  </DropdownMenuItem>
                  {canDelete() && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => setShowDeleteDialog(true)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        삭제
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Comment Body or Edit Form */}
          {isEditing ? (
            <EditCommentForm
              author={comment.author}
              permlink={comment.permlink}
              parentAuthor={comment.parentAuthor}
              parentPermlink={comment.parentPermlink}
              rootAuthor={effectiveRootAuthor}
              rootPermlink={effectiveRootPermlink}
              initialBody={comment.body}
              onSuccess={handleEditSuccess}
              onCancel={() => setIsEditing(false)}
            />
          ) : (
            <div className="mt-2 text-sm prose prose-sm dark:prose-invert max-w-none">
              <CommentBody body={comment.body} />
            </div>
          )}

          {/* Comment Stats and Actions */}
          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <ArrowUp className="h-3 w-3" />
              <span>{comment.upvoteCount}</span>
            </div>
            {comment.childCount > 0 && (
              <div className="flex items-center gap-1">
                <MessageCircle className="h-3 w-3" />
                <span>{comment.childCount}</span>
              </div>
            )}
            {comment.payout > 0 && (
              <div className="flex items-center gap-1">
                <DollarSign className="h-3 w-3" />
                <span>${comment.payout.toFixed(2)}</span>
              </div>
            )}
            {/* Reply Button */}
            {isAuthenticated && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => setShowReplyForm(!showReplyForm)}
              >
                <Reply className="h-3 w-3 mr-1" />
                답글
              </Button>
            )}
          </div>

          {/* Reply Form */}
          {showReplyForm && (
            <ReplyForm
              parentAuthor={comment.author}
              parentPermlink={comment.permlink}
              rootAuthor={effectiveRootAuthor}
              rootPermlink={effectiveRootPermlink}
              onSuccess={handleReplySuccess}
              onCancel={() => setShowReplyForm(false)}
            />
          )}

          {/* Nested Replies */}
          {showReplies && comment.children && comment.children.length > 0 && (
            <div className="mt-4 pl-4 border-l-2 border-border space-y-0">
              {comment.children.map((child) => (
                <CommentItem
                  key={`${child.author}-${child.permlink}`}
                  comment={child}
                  showReplies={showReplies}
                  rootAuthor={effectiveRootAuthor}
                  rootPermlink={effectiveRootPermlink}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteCommentDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        permlink={comment.permlink}
        rootAuthor={effectiveRootAuthor}
        rootPermlink={effectiveRootPermlink}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  )
}

function CommentBody({ body }: { body: string }) {
  // Simple markdown-like rendering for comments
  const processedBody = body
    // Convert markdown images to img tags
    .replace(
      /!\[([^\]]*)\]\(([^)]+)\)/g,
      '<img src="$2" alt="$1" class="max-w-full rounded-lg my-2 max-h-64" />'
    )
    // Convert markdown links to anchor tags
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$1</a>'
    )
    // Convert user mentions to links
    .replace(
      /@([a-z][a-z0-9.-]{2,15})/g,
      '<a href="/@$1" class="text-primary hover:underline">@$1</a>'
    )
    // Convert newlines to br tags
    .replace(/\n/g, '<br />')

  return (
    <div
      className="whitespace-pre-wrap break-words"
      dangerouslySetInnerHTML={{ __html: processedBody }}
    />
  )
}
